"use client";

import { c as _c } from "react/compiler-runtime";
import { isInOnboardingWindow, useSetupReadiness } from "@/hooks/use-setup-capability";
import type { CapabilityReadiness, SetupCapability, SetupCapabilityState } from "@/types/clinic-setup";
const CAPABILITY_LABELS: Record<string, string> = {
  PAYMENTS: "payments",
  POS_TERMINAL: "card terminal",
  TELEPHONY: "texting",
  EMAIL: "email"
};

/** "a", "a and b", "a, b and c" */
function joinLabels(labels: string[]): string {
  if (labels.length <= 1) return labels[0] ?? "";
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}
function labelsFor(entries: CapabilityReadiness[]): string {
  return joinLabels(entries.map(entry => CAPABILITY_LABELS[entry.capability] ?? entry.capability.toLowerCase()));
}
function sentence(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
export interface SetupChecklistItem {
  capability: SetupCapability;
  label: string;
  state: SetupCapabilityState;
  note?: string;
  target?: string;
}
export interface SetupNag {
  isComplete: boolean;
  daysToGoLive: number;
  shortLabel: string;
  summary: string;
  items: SetupChecklistItem[];
  completedCount: number;
  totalCount: number;
}
const STATE_LABELS: Record<SetupCapabilityState, string> = {
  ACTION_NEEDED: "Action needed",
  NOT_STARTED: "Not started",
  PENDING: "Blocked",
  READY: "Completed"
};
const STATE_ORDER: Record<SetupCapabilityState, number> = {
  ACTION_NEEDED: 0,
  NOT_STARTED: 1,
  PENDING: 2,
  READY: 3
};

/** Tie-break inside a status group — what matters most to a clinic first. */
const CAPABILITY_ORDER: Record<string, number> = {
  PAYMENTS: 0,
  // Directly under payments: the two together are "can you take money".
  POS_TERMINAL: 1,
  TELEPHONY: 2,
  EMAIL: 3
};

/** Whether to nag this clinic about unfinished onboarding, and what to say. */
export function useSetupNag() {
  const $ = _c(16);
  const {
    data
  } = useSetupReadiness();
  const daysToGoLive = data?.progress.daysToGoLive ?? null;
  if (!data || !isInOnboardingWindow(data) || daysToGoLive === null) {
    return null;
  }
  let clauses;
  let isComplete;
  let items;
  let t0;
  let t1;
  let totalCount;
  if ($[0] !== data.capabilities || $[1] !== data.progress) {
    t1 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const capabilities = Object.values(data.capabilities).filter(_temp);
      const actionNeeded = capabilities.filter(_temp2);
      const notStarted = capabilities.filter(_temp3);
      const pending = capabilities.filter(_temp4);
      isComplete = capabilities.every(_temp5);
      if (isComplete && data.progress.completionDismissed) {
        t1 = null;
        break bb0;
      }
      clauses = [];
      actionNeeded.forEach(entry_4 => {
        if (entry_4.detail) {
          clauses.push(entry_4.detail);
        }
      });
      if (actionNeeded.length > 0 && clauses.length === 0) {
        clauses.push(`Your ${labelsFor(actionNeeded)} setup needs attention.`);
      }
      if (notStarted.length > 0) {
        clauses.push(`You haven't set up ${labelsFor(notStarted)} yet.`);
      }
      if (pending.length > 0) {
        clauses.push(sentence(`${labelsFor(pending)} ${pending.length === 1 ? "is" : "are"} still being reviewed.`));
      }
      if (clauses.length === 0) {
        clauses.push(isComplete ? "Everything is set up and ready to use." : "Your clinic setup isn't finished yet.");
      }
      const ordered = [...capabilities].sort(_temp6);
      items = ordered.map(_temp7);
      totalCount = items.length;
      t0 = items.filter(_temp8);
    }
    $[0] = data.capabilities;
    $[1] = data.progress;
    $[2] = clauses;
    $[3] = isComplete;
    $[4] = items;
    $[5] = t0;
    $[6] = t1;
    $[7] = totalCount;
  } else {
    clauses = $[2];
    isComplete = $[3];
    items = $[4];
    t0 = $[5];
    t1 = $[6];
    totalCount = $[7];
  }
  if (t1 !== Symbol.for("react.early_return_sentinel")) {
    return t1;
  }
  const completedCount = t0.length;
  const t2 = `${completedCount}/${totalCount}`;
  const t3 = clauses.join(" ");
  let t4;
  if ($[8] !== completedCount || $[9] !== daysToGoLive || $[10] !== isComplete || $[11] !== items || $[12] !== t2 || $[13] !== t3 || $[14] !== totalCount) {
    t4 = {
      isComplete,
      daysToGoLive,
      shortLabel: t2,
      summary: t3,
      items,
      completedCount,
      totalCount
    };
    $[8] = completedCount;
    $[9] = daysToGoLive;
    $[10] = isComplete;
    $[11] = items;
    $[12] = t2;
    $[13] = t3;
    $[14] = totalCount;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}
function _temp8(item) {
  return item.state === "READY";
}
function _temp7(entry_5) {
  return {
    capability: entry_5.capability,
    label: sentence(CAPABILITY_LABELS[entry_5.capability] ?? entry_5.capability.toLowerCase()),
    state: entry_5.state,
    note: STATE_LABELS[entry_5.state],
    target: entry_5.remediationPath ?? undefined
  };
}
function _temp6(a, b) {
  return STATE_ORDER[a.state] - STATE_ORDER[b.state] || (CAPABILITY_ORDER[a.capability] ?? 99) - (CAPABILITY_ORDER[b.capability] ?? 99);
}
function _temp5(entry_3) {
  return entry_3.state === "READY";
}
function _temp4(entry_2) {
  return entry_2.state === "PENDING";
}
function _temp3(entry_1) {
  return entry_1.state === "NOT_STARTED";
}
function _temp2(entry_0) {
  return entry_0.state === "ACTION_NEEDED";
}
function _temp(entry) {
  return Boolean(entry);
}